import { Inject, Injectable } from '@angular/core';
import { Articolo } from '@models/articolo.model';
import { NgxCrudService } from '@sinapsys/ngx-crud';
import { Observable, map } from 'rxjs';
import { COMMON_APP_CONFIG } from '../tokens/common-config.token';
import { LettoreSse } from '@shared/utils/sse-client.util';

/** Avanzamento di una chiamata AI in streaming. */
export type EventoAI =
  /** La richiesta e' partita: e' il primo byte, quello che disarma il 524. */
  | { tipo: 'inizio' }
  /** Il modello sta ragionando e non ha ancora scritto niente. */
  | { tipo: 'attesa'; caratteriRagionati: number }
  /** Un pezzo di testo appena scritto dal modello. */
  | { tipo: 'pezzo'; testo: string }
  /** Risposta completa e verificata dal backend. */
  | { tipo: 'fine'; result: string };

@Injectable({
  providedIn: 'root',
})
export class AIService extends NgxCrudService<Articolo, number> {
  constructor(@Inject(COMMON_APP_CONFIG) private mainConfig: Required<{ api: { apiBaseUrl: string } }>) {
    super({ apiBaseUrl: '' });
    this._baseUrl += this.mainConfig.api.apiBaseUrl + 'ai/';
  }

  /**
   * reasoningEffort regola quanto il modello ragiona prima di rispondere: e' la
   * parte lenta della chiamata. Su un compito meccanico come la formattazione
   * il ragionamento non serve e costa tutto il tempo di attesa: 'none' porta un
   * articolo di 3 KB da 68 secondi a 7, e 'low' e 'minimal' non aiutano.
   */
  callAI(
    text: string,
    reasoningEffort?: 'none' | 'minimal' | 'low' | 'medium' | 'high'
  ): Observable<string> {
    return this.http.post<string>(this._baseUrl + 'call-gpt', { text, reasoningEffort }, { withCredentials: true });
  }

  /**
   * Come callAI, ma la risposta arriva a pezzi.
   *
   * Da usare dove il ragionamento del modello serve e quindi l'attesa e' lunga:
   * riscrivere un articolo costa fino a 166 secondi, e Cloudflare chiude a 100
   * la richiesta che non ha ancora risposto nulla. HttpClient consegna il corpo
   * solo a risposta finita, percio' qui si legge con fetch.
   *
   * L'Observable emette gli eventi in ordine e si chiude su 'fine'; se qualcosa
   * va storto emette un errore con il messaggio del backend, come le altre
   * chiamate. Annullare la sottoscrizione interrompe anche la richiesta, e il
   * backend smette di consumare token.
   */
  callAIStream(
    text: string,
    reasoningEffort?: 'none' | 'minimal' | 'low' | 'medium' | 'high'
  ): Observable<EventoAI> {
    return new Observable<EventoAI>((subscriber) => {
      const controller = new AbortController();
      const lettore = new LettoreSse();

      (async () => {
        let risposta: Response;
        try {
          risposta = await fetch(this._baseUrl + 'call-gpt-stream', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
            body: JSON.stringify({ text, reasoningEffort }),
            signal: controller.signal,
          });
        } catch (err) {
          // Rete assente o richiesta annullata prima di partire.
          subscriber.error({ status: 0, error: err });
          return;
        }

        if (!risposta.ok || !risposta.body) {
          let messaggio: string | undefined;
          try {
            messaggio = (await risposta.json())?.messaggio;
          } catch {
            // Un 502 di Apache o un 524 di Cloudflare rispondono HTML: nessun
            // messaggio da leggere, ci pensa chi gestisce l'errore.
          }
          subscriber.error({ status: risposta.status, error: messaggio ? { messaggio } : null });
          return;
        }

        const reader = risposta.body.getReader();
        const decoder = new TextDecoder();
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }
            for (const evento of lettore.aggiungi(decoder.decode(value, { stream: true }))) {
              if (evento.nome === 'errore') {
                subscriber.error({ status: 502, error: { messaggio: evento.dati?.messaggio } });
                return;
              }
              if (evento.nome === 'inizio') {
                subscriber.next({ tipo: 'inizio' });
              } else if (evento.nome === 'attesa') {
                subscriber.next({ tipo: 'attesa', caratteriRagionati: evento.dati?.caratteriRagionati ?? 0 });
              } else if (evento.nome === 'pezzo') {
                subscriber.next({ tipo: 'pezzo', testo: String(evento.dati?.testo ?? '') });
              } else if (evento.nome === 'fine') {
                subscriber.next({ tipo: 'fine', result: String(evento.dati?.result ?? '') });
                subscriber.complete();
                return;
              }
            }
          }
          // Lo stream e' finito senza l'evento 'fine': la connessione e' caduta
          // a metà, e il testo che il pannello ha ricevuto non e' completo.
          subscriber.error({ status: 0, error: null });
        } catch (err) {
          if (!controller.signal.aborted) {
            subscriber.error({ status: 0, error: err });
          }
        }
      })();

      return () => controller.abort();
    });
  }

  socialPublish(url: string, id: string): Observable<string> {
    return this.http.post<string>(this._baseUrl + 'social-publish', { url, id }, { withCredentials: true });
  }
}
